import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";

const defaultDraft = {
  projectNumberStart: "",
  presentationNumberStart: "",
  nextPoNumberStart: "",
  salesOrderNumberStart: "",
  autoAddProjectNumberToPos: false,
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function ProjectNumbersTab({ instanceUniqueId }) {
  const [draft, setDraft] = useState(defaultDraft);
  const [saving, setSaving] = useState(false);
  const [instanceId, setInstanceId] = useState(null);
  const [nextNumbers, setNextNumbers] = useState(null);

  const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
  const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };

  const fetchInstanceId = async () => {
    if (!instanceUniqueId) return;
    const res = await axios.get(
      `${SERVER}/instances/byinstanceUniqueId/${instanceUniqueId}`,
      headers
    );
    setInstanceId(res.data?.id || null);
  };

  const fetchSettings = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    const res = await axios.get(
      `${SERVER}/instances/${targetInstanceId}/project-numbers`,
      headers
    );
    const settings = res.data?.settings || {};
    setDraft({
      projectNumberStart: settings.projectNumberStart ?? "",
      presentationNumberStart: settings.presentationNumberStart ?? "",
      nextPoNumberStart: settings.nextPoNumberStart ?? "",
      salesOrderNumberStart: settings.salesOrderNumberStart ?? "",
      autoAddProjectNumberToPos: !!settings.autoAddProjectNumberToPos,
    });
  };

  const fetchNextNumbers = async (targetInstanceId = instanceId) => {
    if (!targetInstanceId) return;
    try {
      const res = await axios.get(
        `${SERVER}/instances/${targetInstanceId}/project-numbers/next`,
        headers
      );
      setNextNumbers(res.data || null);
    } catch (error) {
      setNextNumbers(null);
    }
  };

  useEffect(() => {
    fetchInstanceId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceUniqueId]);

  useEffect(() => {
    if (instanceId) {
      fetchSettings(instanceId);
      fetchNextNumbers(instanceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId]);

  const save = async () => {
    if (!instanceId) return;
    setSaving(true);
    try {
      await axios.put(
        `${SERVER}/instances/${instanceId}/project-numbers`,
        {
          projectNumberStart: toNumberOrNull(draft.projectNumberStart),
          presentationNumberStart: toNumberOrNull(draft.presentationNumberStart),
          nextPoNumberStart: toNumberOrNull(draft.nextPoNumberStart),
          salesOrderNumberStart: toNumberOrNull(draft.salesOrderNumberStart),
          autoAddProjectNumberToPos: !!draft.autoAddProjectNumberToPos,
        },
        headers
      );
      fetchSettings(instanceId);
    } finally {
      setSaving(false);
    }
  };

  const canSave = useMemo(() => {
    const values = [
      draft.projectNumberStart,
      draft.presentationNumberStart,
      draft.nextPoNumberStart,
      draft.salesOrderNumberStart,
    ];
    return values.every((value) => value === "" || Number.isFinite(Number(value)));
  }, [draft]);

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Project Number Settings
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Set the next numbers for new records created in this instance.
        </Typography>
      </Box>

      <Box sx={{ display: "grid", gap: 1.5 }}>
        <TextField
          label="Next Project Number"
          type="number"
          value={draft.projectNumberStart}
          onChange={(e) => setDraft((prev) => ({ ...prev, projectNumberStart: e.target.value }))}
          placeholder="81238"
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Next in system: {nextNumbers?.nextProjectNumber ?? "—"}
        </Typography>
        <TextField
          label="Next Presentation Number"
          type="number"
          value={draft.presentationNumberStart}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, presentationNumberStart: e.target.value }))
          }
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Next in system: {nextNumbers?.nextPresentationNumber ?? "—"}
        </Typography>
        <TextField
          label="Next Purchase Order Number"
          type="number"
          value={draft.nextPoNumberStart}
          onChange={(e) => setDraft((prev) => ({ ...prev, nextPoNumberStart: e.target.value }))}
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Next in system: {nextNumbers?.nextPoNumber ?? "—"}
        </Typography>
        <TextField
          label="Next Sales Order Number"
          type="number"
          value={draft.salesOrderNumberStart}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, salesOrderNumberStart: e.target.value }))
          }
        />
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Next in system: {nextNumbers?.nextSalesOrderNumber ?? "—"}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Switch
          checked={!!draft.autoAddProjectNumberToPos}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, autoAddProjectNumberToPos: e.target.checked }))
          }
        />
        <Typography sx={{ fontWeight: 700 }}>Auto-add project numbers to POs</Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={save} disabled={!canSave || saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
}
